import { Box, CircularProgress, Divider, Icon, Typography, Link, Skeleton } from '@mui/material'
import AdxDrawerComponent from 'components/reusable/drawer/AdxDrawerComponent'
import AdxTabsBadge from 'components/reusable/tab/AdxTabsBadge'
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import React, { useEffect, useState } from 'react'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import DeleteTalentModal from './DeleteTalentModal'
import KeepTalentModal from './KeepTalentModal'
import EditDetailsModal from './EditDetailsModal'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { getCandidate, updateCandidate } from 'services/api/modules/recruitment/external-talent'
import { format } from 'date-fns'
import ScoreCard from '../../recruitment-manage-applicant/components/ScoreCard'
import { Star } from '@mui/icons-material'
import { downloadBlobFile, notifyDownloadError } from 'services/helper/fileDownload'

const ViewTalentDrawer = ({ talentId, handleRemove, handleKeep, isKeepable = false }) => {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false) // EditDetailsModal
  const [drawerState, setDrawerState] = useState({ right: false })
  const selectedId = [talentId] // For single deletion use

  const { data, isLoading, refetch, isFetching } = useQuery(
    'getCandidate',
    async () => {
      const { data } = await getCandidate(talentId)
      return data
    },
    {
      refetchOnWindowFocus: false,
      enabled: drawerState.right,
    }
  )

  const { mutate: editDetails, error: updateError } = useMutation(
    (data) => updateCandidate(talentId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('getCandidate')
        queryClient.invalidateQueries('getAllCandidate')
        setOpen(false)
      },
      onError: (error) => {
        console.error(error)
      },
    }
  )

  const score_data = data?.profile_scores

  const handleDrawer = (anchor, open) => () => {
    setDrawerState({ ...drawerState, [anchor]: open })
  }

  const [isDownloading, setIsDownloading] = useState(false)
  const handleDownload = (fileURL, fileName = 'resume') => {
    const renamedFile = fileName !== '' ? fileRename(fileName) : `talent_${talentId}`

    setIsDownloading(true)
    fetch(fileURL)
      .then((response) => response.blob())
      .then((blob) => {
        const contentType = blob.type || 'application/octet-stream'
        downloadBlobFile({
          data: blob,
          fileName: renamedFile,
          contentType,
          successMessage: 'Talent file downloaded.',
        })
        setIsDownloading(false)
      })
      .catch((error) => {
        console.error('Error downloading the file:', error)
        notifyDownloadError('Unable to download talent file. Please try again.')
        setIsDownloading(false)
      })
  }

  const fileRename = (inputString) => {
    const renamedString = inputString?.replace(/[ '"`]/g, '_').toLowerCase()
    return renamedString
  }

  const calcExpDuration = (months) => {
    if (months > 24) {
      return `${Math.floor(months / 12)} ${Math.floor(months / 12) > 1 ? 'yrs' : 'yr'} ${
        months % 12
      } ${months % 12 > 1 ? 'mos' : 'mo'}`
    } else return `${months} ${months > 1 ? 'mos' : 'mo'}`
  }

  const reformatDate = (date) => {
    const parsedDate = new Date(date)
    const formattedDate = format(parsedDate, 'dd/MM/yyyy, hh:mm a')
    return formattedDate
  }

  const shortListed = !data?.is_deleteable
  const incompleteDetails = !data?.name || !data?.email || !data?.contact
  const skillSet = data?.skills.split(',')

  useEffect(() => {
    if (talentId && drawerState.right) {
      refetch()
    }
  }, [drawerState.right])

  const [talentDetails, setTalentDetails] = useState({
    name: data?.name,
    email: data?.email,
    phone: data?.contact,
  })

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
    setTalentDetails({
      name: data?.name,
      email: data?.email,
      phone: data?.contact,
    })
  }

  const jobFitText = ['Minimum fit', 'Partially fit', 'Averagely fit', 'Well fit', 'Strongly fit']

  return (
    <Box>
      <Typography
        onClick={handleDrawer('right', true)}
        sx={{ color: 'alternate', textDecoration: 'underline', cursor: 'pointer' }}
      >
        View
      </Typography>
      <AdxDrawerComponent
        state={drawerState}
        anchor="right"
        onClose={handleDrawer}
        sx={{ padding: 0 }}
        sxHeader={{ padding: '15px 30px' }}
        headerprops={
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography sx={{ fontWeight: 'bold' }}>Talent</Typography>
            {!shortListed && (
              <DeleteTalentModal
                variant="icon"
                selectedIds={selectedId}
                handleRemove={handleRemove}
                handleDrawer={handleDrawer('right', false)}
              />
            )}
          </Box>
        }
      >
        <Divider />
        <>
          {/* If shortListed */}
          {shortListed && (
            <Box
              sx={{
                backgroundColor: 'default-background.main',
                padding: '14px 30px',
                color: 'light-gray-text.main',
              }}
            >
              <Typography>
                {isKeepable
                  ? 'This candidate is currently in your job hiring process. You may remove other talents to resolve redundancy.'
                  : 'This candidate is currently in your job hiring process.'}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              padding: '30px',
              display: 'flex',
              alignItems: !shortListed && isKeepable ? 'flex-start' : 'center',
              gap: '20px',
            }}
          >
            <Box
              sx={{
                width: '65px',
                height: '65px',
                backgroundColor: 'default-background.main',
                borderRadius: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {isLoading || isFetching ? (
                <Skeleton variant="circular" width="60px" height="60px" />
              ) : (
                <Icon sx={{ fontSize: '36px', color: 'alternate.main' }}>person_outline</Icon>
              )}
            </Box>
            <Box sx={{ width: 'calc(100% - 80px)' }}>
              {isLoading || isFetching ? (
                <Skeleton variant="rounded" width="100%" height={60} />
              ) : (
                <>
                  <Typography variant="h4">
                    {data?.name !== '' ? data?.name : `Talent ${talentId}`}
                  </Typography>
                  <Typography sx={{ color: 'light-gray-text.main' }}>
                    {data?.email !== '' ? data?.email : '-'}
                  </Typography>
                </>
              )}
              {!shortListed && isKeepable && (
                <Box sx={{ width: '100%', display: 'flex', gap: '10px', marginTop: '15px' }}>
                  <KeepTalentModal handleKeep={handleKeep} />
                  <DeleteTalentModal
                    variant="outlined"
                    selectedIds={selectedId}
                    handleRemove={handleRemove}
                    handleDrawer={handleDrawer('right', false)}
                  />
                </Box>
              )}
            </Box>
          </Box>
          <AdxTabsBadge
            initialValue={0}
            sxPanel={{ borderRadius: 0, borderTop: '1px solid #E5E5E5' }}
          >
            {data?.job_matches?.length ? (
              <AdxTabsPanel label="Job matches">
                <Box
                  sx={{
                    margin: '30px',
                    border: '1px solid',
                    borderColor: 'gray-line.main',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: '4.5px',
                  }}
                >
                  {data?.job_matches.map((job, index) => (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        py: '20px',
                        px: '16px',
                        borderBottom: '1px solid',
                        borderColor: 'gray-line.main',
                      }}
                    >
                      <Box>
                        <Typography sx={{ mb: '4px' }}>{job?.job_title}</Typography>
                        <Typography variant="subtitle2" sx={{ color: 'light-gray-text.main' }}>
                          Date matched {format(new Date(job?.date_matched), 'dd/MM/yyyy')}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: '4px' }}>
                          {new Array(job.average_job_fit_score).fill(0).map((_, index) => (
                            <Star sx={{ fontSize: '16px', color: 'yellow-01.main' }} />
                          ))}
                          {new Array(5 - job.average_job_fit_score).fill(0).map((_, index) => (
                            <Star sx={{ fontSize: '16px', color: 'gray-inactive-button.main' }} />
                          ))}
                        </Box>
                        <Typography>{jobFitText[job.average_job_fit_score - 1]}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </AdxTabsPanel>
            ) : null}
            <AdxTabsPanel label="Resume">
              <Box sx={{ padding: '30px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Box
                  sx={{
                    height: '24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  {isLoading || isFetching ? (
                    <Skeleton variant="rounded" width="50%" height="100%" />
                  ) : (
                    <Typography sx={{ color: 'light-gray-text.main' }}>
                      Uploaded on{' '}
                      {data?.cv_upload_date ? reformatDate(data?.cv_upload_date) : '...'}
                    </Typography>
                  )}
                  {isDownloading ? (
                    <CircularProgress color="alternate" size={20} />
                  ) : isLoading || isFetching ? (
                    <Skeleton variant="rounded" height={20} width={20} />
                  ) : (
                    <FileDownloadOutlinedIcon
                      onClick={() => handleDownload(data?.cv_url, data?.name)}
                      sx={{ fontSize: '24px', color: 'alternate.main', cursor: 'pointer' }}
                    />
                  )}
                </Box>
                {isLoading || isFetching ? (
                  <Skeleton variant="rounded" height="240px" width="100%" />
                ) : (
                  <Box
                    sx={{ borderRadius: '4.5px', overflow: 'clip', border: '1px solid #E5E5E5' }}
                  >
                    <Box
                      sx={{
                        height: '200px',
                        backgroundColor: 'default-background.main',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Icon sx={{ color: 'alternate.main', fontSize: '34px' }}>description</Icon>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px',
                        borderTop: '1px solid #E5E5E5',
                      }}
                    >
                      <Box>
                        <Typography>
                          {data?.name !== '' ? fileRename(data?.name) : `talent_${talentId}`}.pdf
                        </Typography>
                        <Typography variant="subtitle2" sx={{ color: 'light-gray-text.main' }}>
                          23.3 mb
                        </Typography>
                      </Box>
                      <Link target="_blank" href={data?.cv_url} sx={{ color: 'alternate.main' }}>
                        View
                      </Link>
                    </Box>
                  </Box>
                )}
              </Box>
            </AdxTabsPanel>
            <AdxTabsPanel label="Details">
              <Box sx={{ padding: '30px' }}>
                {incompleteDetails && (
                  <Box
                    sx={{
                      marginBottom: '24.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      borderRadius: '4.5px',
                      backgroundColor: 'light-warning.main',
                    }}
                  >
                    <Icon sx={{ color: 'warning.main', fontSize: '20px' }}>error</Icon>
                    <Typography>
                      Some details couldn't be parsed. You can add or edit the details.
                    </Typography>
                  </Box>
                )}
                <Typography sx={{ marginBottom: '18.5px', fontWeight: 'bold' }}>Details</Typography>
                <Box sx={{ display: 'flex', gap: '14px', marginBottom: '48.5px' }}>
                  <Box
                    sx={{
                      width: '150px',
                      color: 'light-gray-text.main',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                    }}
                  >
                    <Typography>Name</Typography>
                    <Typography>Email</Typography>
                    <Typography>Contact number</Typography>
                    {/* <Typography>LinkedIn</Typography> */}
                  </Box>
                  <Box
                    sx={{
                      width: 'calc(100% - 164px)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                    }}
                  >
                    {data?.name ? (
                      <Box
                        onClick={handleToggle}
                        sx={{
                          color: 'transparent',
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                          cursor: 'pointer',
                          '&:hover': { color: 'alternate.main' },
                        }}
                      >
                        <Typography
                          sx={{ color: 'dark-text.main', '&:hover': { color: 'alternate.main' } }}
                        >
                          {data?.name}
                        </Typography>
                        <Icon sx={{ fontSize: '16px' }}>edit</Icon>
                      </Box>
                    ) : (
                      <Box
                        onClick={handleToggle}
                        sx={{
                          width: 'fit-content',
                          color: 'alternate.main',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1px',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                      >
                        <Icon sx={{ fontSize: '16px' }}>add</Icon>
                        <Typography>Add</Typography>
                      </Box>
                    )}
                    {data?.email ? (
                      <Box
                        onClick={handleToggle}
                        sx={{
                          color: 'transparent',
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                          cursor: 'pointer',
                          '&:hover': { color: 'alternate.main' },
                        }}
                      >
                        <Typography
                          sx={{ color: 'dark-text.main', '&:hover': { color: 'alternate.main' } }}
                        >
                          {data?.email}
                        </Typography>
                        <Icon sx={{ fontSize: '16px' }}>edit</Icon>
                      </Box>
                    ) : (
                      <Box
                        onClick={handleToggle}
                        sx={{
                          width: 'fit-content',
                          color: 'alternate.main',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1px',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                      >
                        <Icon sx={{ fontSize: '16px' }}>add</Icon>
                        <Typography>Add</Typography>
                      </Box>
                    )}
                    {data?.contact ? (
                      <Box
                        onClick={handleToggle}
                        sx={{
                          color: 'transparent',
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                          cursor: 'pointer',
                          '&:hover': { color: 'alternate.main' },
                        }}
                      >
                        <Typography
                          sx={{ color: 'dark-text.main', '&:hover': { color: 'alternate.main' } }}
                        >
                          {data?.contact}
                        </Typography>
                        <Icon sx={{ fontSize: '16px' }}>edit</Icon>
                      </Box>
                    ) : (
                      <Box
                        onClick={handleToggle}
                        sx={{
                          width: 'fit-content',
                          color: 'alternate.main',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '1px',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        }}
                      >
                        <Icon sx={{ fontSize: '16px' }}>add</Icon>
                        <Typography>Add</Typography>
                      </Box>
                    )}
                    <EditDetailsModal
                      error={updateError}
                      editDetails={editDetails}
                      talentId={talentId}
                      defaultValues={data}
                      open={open}
                      talentDetails={talentDetails}
                      setTalentDetails={setTalentDetails}
                      handleToggle={handleToggle}
                    />
                  </Box>
                </Box>
                <Typography sx={{ marginBottom: '18.5px', fontWeight: 'bold' }}>Skills</Typography>
                {skillSet?.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {skillSet?.map((i, index) => (
                      <Box
                        key={index}
                        sx={{
                          borderRadius: '4.5px',
                          backgroundColor: 'default-background.main',
                          padding: '12px',
                        }}
                      >
                        {i}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      flexDirection: 'column',
                      gap: '20px',
                    }}
                  >
                    <Box
                      sx={{
                        width: '90px',
                        height: '90px',
                        borderRadius: '100%',
                        backgroundColor: 'default-background.main',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Icon sx={{ width: '35px', height: '35px', color: 'light-gray-text.main' }}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z"
                          />
                        </svg>
                      </Icon>
                    </Box>
                    <Typography variant="h4" color="light-gray-text.main">
                      No information on skills
                    </Typography>
                  </Box>
                )}
              </Box>
            </AdxTabsPanel>
            <AdxTabsPanel label="Work experience">
              {data?.experiences?.length > 0 ? (
                data?.experiences?.map((i, index) => (
                  <Box
                    key={index}
                    sx={{
                      padding: '30px',
                      borderTop: index === 0 ? 'none' : '1px solid #E5E5E5',
                    }}
                  >
                    <Typography sx={{ marginBottom: '20px', fontWeight: 'bold' }}>
                      {i.position}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '14px', marginBottom: '10px' }}>
                      <Box
                        sx={{
                          width: '150px',
                          color: 'light-gray-text.main',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px',
                        }}
                      >
                        <Typography>Company</Typography>
                        <Typography>Duration</Typography>
                        <Typography>Salary</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <Typography>{i.company_name ?? '-'}</Typography>
                        <Typography>{`${i.start_month}/${i.start_year} - ${
                          i.current_job === 1 ? 'PRESENT' : `${i.end_month}/${i.end_year}`
                        } (${calcExpDuration(i.duration)})`}</Typography>

                        <Typography>{i.salary ?? '-'}</Typography>
                      </Box>
                    </Box>
                    <Typography sx={{ color: 'light-gray-text.main', marginBottom: '4px' }}>
                      Job Description
                    </Typography>
                    <Typography>{i.job_description ?? '-'}</Typography>
                  </Box>
                ))
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '50vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: '20px',
                  }}
                >
                  <Box
                    sx={{
                      width: '90px',
                      height: '90px',
                      borderRadius: '100%',
                      backgroundColor: 'default-background.main',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Icon sx={{ width: '35px', height: '35px', color: 'light-gray-text.main' }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z"
                        />
                      </svg>
                    </Icon>
                  </Box>
                  <Typography variant="h4" color="light-gray-text.main">
                    No information on experience
                  </Typography>
                </Box>
              )}
            </AdxTabsPanel>
            <AdxTabsPanel label="Education">
              {data?.educations?.length > 0 ? (
                data?.educations?.map((i, index) => (
                  <Box
                    key={index}
                    sx={{
                      padding: '30px',
                      borderTop: index === 0 ? 'none' : '1px solid #E5E5E5',
                    }}
                  >
                    <Typography sx={{ marginBottom: '20px', fontWeight: 'bold' }}>
                      {i.academic_field}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '14px' }}>
                      <Box
                        sx={{
                          width: '150px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '10px',
                          color: 'light-gray-text.main',
                        }}
                      >
                        {i.start_year && <Typography>Tenure</Typography>}
                        <Typography>College</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {i.start_year && (
                          <Typography>{`${i.start_year} - ${i.end_year}`}</Typography>
                        )}
                        <Typography>{i.university}</Typography>
                      </Box>
                    </Box>
                  </Box>
                ))
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '50vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: '20px',
                  }}
                >
                  <Box
                    sx={{
                      width: '90px',
                      height: '90px',
                      borderRadius: '100%',
                      backgroundColor: 'default-background.main',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Icon sx={{ width: '35px', height: '35px', color: 'light-gray-text.main' }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5"
                        />
                      </svg>
                    </Icon>
                  </Box>
                  <Typography variant="h4" color="light-gray-text.main">
                    No information on education
                  </Typography>
                </Box>
              )}
            </AdxTabsPanel>
            {/* hide score tab */}
            {/* <AdxTabsPanel label="Score">
              <ScoreCard data={score_data} total_score={data?.profile_scores?.total_score} />
            </AdxTabsPanel> */}
          </AdxTabsBadge>
        </>
      </AdxDrawerComponent>
    </Box>
  )
}

export default ViewTalentDrawer
